import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslator } from '@picsart/rc/services/localization';

import {
  BATCH_TOOLS,
  inputParams,
  POSITIVE_NUMBER_REGEXP,
  RESIZE_SIDE_ENUM,
  resizeMaxSize,
  resizeMinSize,
} from 'pages/Batch/configs';
import { DataActionTypes } from 'pages/Batch/sections/Context';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';

import useStyles from './styles';

interface IInputSize {
  values: number[];
  onChangeValue: Dispatch<SetStateAction<number[]>>;
  activeSide: RESIZE_SIDE_ENUM;
}

const InputSizes = ({ values, onChangeValue, activeSide }: IInputSize) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { dispatchData, resizeParams } = useBatchData();

  const [isDefaultParamsChanged, setisDefaultParamsChanged] = useState(false);

  const handleChangeSize = useCallback(({ target }: ChangeEvent<HTMLInputElement>, sizeIndex) => {
    const { value } = target;
    if (!POSITIVE_NUMBER_REGEXP.test(value)) {
      return;
    }
    onChangeValue(prev => prev.map((item, i) => (sizeIndex === i ? Number(value) : item)));
  }, [onChangeValue]);

  const onBlur = useCallback(e => e.target?.blur(), []);

  const handleResizeByInput = useCallback(() => {
    const isExceedsLimit = values.some(item => item < resizeMinSize || item > resizeMaxSize);
    const limitedParams = values.map(item => (Number(item) < resizeMinSize
      ? resizeMinSize
      : (item > resizeMaxSize ? resizeMaxSize : item)
    ));
    if (isExceedsLimit) {
      onChangeValue(limitedParams);
    }
    dispatchData({
      type: DataActionTypes.SET_RESIZE_PARAMS,
      params: {
        ...resizeParams,
        isOriginal: false,
        width: [RESIZE_SIDE_ENUM.width, RESIZE_SIDE_ENUM.exactSize].includes(activeSide) ? limitedParams[0] : undefined,
        height: [RESIZE_SIDE_ENUM.height, RESIZE_SIDE_ENUM.exactSize].includes(activeSide) ? limitedParams[1] : undefined,
      },
    });
    !isDefaultParamsChanged && setisDefaultParamsChanged(true);
  }, [onChangeValue, values, resizeParams, activeSide, dispatchData, isDefaultParamsChanged]);

  useEffect(() => {
    analytics.utils().setSubTool(BATCH_TOOLS.resize, {
      name: SubToolNames.resizeBy,
      value: activeSide,
    });

    if (activeSide === RESIZE_SIDE_ENUM.exactSize && (!resizeParams.width || !resizeParams.height)) {
      return;
    }

    if (!isDefaultParamsChanged && activeSide !== RESIZE_SIDE_ENUM.exactSize) {
      setisDefaultParamsChanged(true);
    }
  }, [resizeParams, activeSide, isDefaultParamsChanged]);

  return (
    <div className={classNames(classes.container, { [classes.singleInput]: activeSide !== RESIZE_SIDE_ENUM.exactSize })}>
      {inputParams.map((item, i) => (
        [RESIZE_SIDE_ENUM.exactSize, item.type].includes(activeSide) ? (
          <div key={item.type}>
            {activeSide !== RESIZE_SIDE_ENUM.exactSize && <p className={classes.inputSide}>{translate('Target {{side}} (px)', { side: translate(item.type) })}</p>}
            <div className={classes.currentRatio}>
              <input
                className={classes.ratioInput}
                value={values[i]}
                onChange={e => handleChangeSize(e, i)}
                onKeyPress={e => e.key === 'Enter' && onBlur(e)}
                onBlur={handleResizeByInput}
                maxLength={4}
              />
              {activeSide === RESIZE_SIDE_ENUM.exactSize && <p className={classes.ratioTitle}>{translate(item.title)}</p>}
            </div>
          </div>
        ) : null
      ))}
    </div>
  );
};

export default InputSizes;
